import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { createAuthTokensService } from '../lib/auth/AuthTokensService';
import { setupAuthInterceptor } from '../lib/auth/authInterceptor';
import { useAuth as useIframeAuth } from '../lib/auth/useAuth';
import { initializeHiddenIframeRefresh } from '../lib/auth/hiddenIframeRefresh';
import { authService } from '../lib/auth/authService';
import { authApi, type User, type LoginRequest, api } from '../lib/api';
import toast from 'react-hot-toast';

interface IframeAuthContextType {
  user: User | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  updateProfile: (data: Partial<User>) => Promise<void>;
  changePassword: (data: { current_password: string; new_password: string }) => Promise<void>;
  // Additional iframe auth properties
  credentials: any;
  rolesFromToken: string[] | null;
  authTenantId: string;
}

const IframeAuthContext = createContext<IframeAuthContextType | undefined>(undefined);

interface IframeAuthProviderProps {
  children: ReactNode;
  authDomain: string;
  apiBaseUrl: string;
  onNavigateToLogin: () => void;
  getQueryParam?: (param: string) => string | null;
  darwinLoginUrl?: string;
}

export const IframeAuthProvider: React.FC<IframeAuthProviderProps> = ({
  children,
  authDomain,
  apiBaseUrl,
  onNavigateToLogin,
  getQueryParam,
  darwinLoginUrl,
}) => {
  const queryClient = useQueryClient();
  const [isInitialized, setIsInitialized] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  // Initialize auth service and interceptor
  useEffect(() => {
    createAuthTokensService(authDomain, darwinLoginUrl);
    
    // Initialize hidden iframe refresh service if darwinLoginUrl is provided
    if (darwinLoginUrl) {
      initializeHiddenIframeRefresh(authDomain, darwinLoginUrl);
    }
    
    setupAuthInterceptor(
      apiBaseUrl,
      authService,
      onNavigateToLogin,
      getQueryParam,
      api
    );
  }, [authDomain, apiBaseUrl, onNavigateToLogin, getQueryParam, darwinLoginUrl]);

  // Use iframe auth hook
  const { credentials, rolesFromToken, authTenantId, isLoading: isIframeLoading, isAuthenticated } = useIframeAuth();

  // Fetch user data when credentials are available
  const {
    data: userData,
    isLoading: isUserLoading,
  } = useQuery(
    ['current-user', credentials?.access_token?.token],
    authApi.getCurrentUser,
    {
      enabled: isAuthenticated && !!credentials?.access_token?.token,
      retry: false,
      onSuccess: (data) => {
        setUser(data);
        setIsInitialized(true);
      },
      onError: (error: any) => {
        console.error('Failed to fetch user:', error);
        if (error.response?.status === 401) {
          // Token is invalid, let the interceptor handle it
          setUser(null);
        }
        setIsInitialized(true);
      },
    }
  );

  // Initialize immediately if no credentials
  useEffect(() => {
    if (!isAuthenticated && !isIframeLoading && !isInitialized) {
      setIsInitialized(true);
    }
  }, [isAuthenticated, isIframeLoading, isInitialized]);

  // Update user when userData changes
  useEffect(() => {
    if (userData) {
      setUser(userData);
    }
  }, [userData]);

  // Clear user when not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setUser(null);
    }
  }, [isAuthenticated]);

  // Login mutation
  const loginMutation = useMutation(authApi.login, {
    onSuccess: async (data) => {
      // Store tokens in iframe
      const authTokensService = createAuthTokensService(authDomain, darwinLoginUrl);

      let accessTokenData;

      // Check if data.access_token is already an object with exp field (Darwin format)
      if (typeof data.access_token === 'object' && 'exp' in data.access_token) {
        accessTokenData = data.access_token;
      } else {
        // access_token is a string JWT
        const tokenString = data.access_token as string;

        // Decode JWT to extract expiration time and other fields
        let exp = '';
        let roles = data.user?.roles || [];
        let tenant_id = data.user?.tenant_id || data.user?.current_tenant?.id || '';

        try {
          const decodedToken = authTokensService.decodeToken(tokenString);
          exp = new Date(decodedToken.exp * 1000).toISOString();
          roles = decodedToken.roles || roles;
          tenant_id = decodedToken.tenant_id || tenant_id;
        } catch (error) {
          console.warn('Failed to decode JWT token during login:', error);
        }

        accessTokenData = {
          token: tokenString,
          roles: roles,
          tenant_id: tenant_id,
          exp: exp,
        };
      }

      await authTokensService.setCredentials({
        access_token: accessTokenData,
        refresh_token: {
          token: data.refresh_token,
          exp: '', // Will be populated from JWT if needed
        },
      });
      
      setUser(data.user);
      queryClient.setQueryData('current-user', data.user);
      toast.success('Successfully logged in');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Login failed');
    },
  });

  // Logout function
  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      queryClient.clear();
      toast.success('Successfully logged out');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Logout failed');
    }
  };

  // Update profile mutation
  const updateProfileMutation = useMutation(authApi.updateProfile, {
    onSuccess: (data) => {
      setUser(data);
      queryClient.setQueryData('current-user', data);
      toast.success('Profile updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation(authApi.changePassword, {
    onSuccess: () => {
      toast.success('Password changed successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to change password');
    },
  });

  // Context methods
  const login = async (credentials: LoginRequest) => {
    await loginMutation.mutateAsync(credentials);
  };

  const updateProfile = async (data: Partial<User>) => {
    await updateProfileMutation.mutateAsync(data);
  };

  const changePassword = async (data: { current_password: string; new_password: string }) => {
    await changePasswordMutation.mutateAsync(data);
  };

  const hasRole = (role: string): boolean => {
    // Check user roles first, fallback to token roles if user not loaded yet
    const userRoles = user?.roles || rolesFromToken || [];
    return userRoles.includes(role);
  };

  const hasAnyRole = (roles: string[]): boolean => {
    // Check user roles first, fallback to token roles if user not loaded yet
    const userRoles = user?.roles || rolesFromToken || [];
    return roles.some(role => userRoles.includes(role));
  };

  // Determine loading state
  const isLoading = 
    !isInitialized ||
    isIframeLoading ||
    (isAuthenticated && isUserLoading) ||
    loginMutation.isLoading ||
    updateProfileMutation.isLoading ||
    changePasswordMutation.isLoading;

  const value: IframeAuthContextType = {
    user,
    login,
    logout,
    isLoading,
    isAuthenticated: isAuthenticated, // Use iframe auth status directly
    hasRole,
    hasAnyRole,
    updateProfile,
    changePassword,
    credentials,
    rolesFromToken,
    authTenantId,
  };

  return (
    <IframeAuthContext.Provider value={value}>
      {children}
    </IframeAuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(IframeAuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an IframeAuthProvider');
  }
  return context;
};

// Role-based access control component
interface RequireAuthProps {
  children: React.ReactNode;
  roles?: string[];
  fallback?: React.ReactNode;
}

export const RequireAuth: React.FC<RequireAuthProps> = ({
  children,
  roles = [],
  fallback = <div>Access denied</div>
}) => {
  const { isLoading, isAuthenticated, hasAnyRole } = useAuth();

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return fallback;
  }

  if (roles.length > 0 && !hasAnyRole(roles)) {
    return fallback;
  }

  return <>{children}</>;
};

// Hook for checking permissions
export const usePermissions = () => {
  const { hasRole, hasAnyRole } = useAuth();

  const canManageUsers = hasRole('admin');
  const canManageContent = hasAnyRole(['admin', 'manager']);
  const canViewReports = hasAnyRole(['admin', 'manager', 'viewer']);
  const canManageSettings = hasRole('admin');

  return {
    canManageUsers,
    canManageContent,
    canViewReports,
    canManageSettings,
    isAdmin: hasRole('admin'),
    isManager: hasRole('manager'),
    isViewer: hasRole('viewer'),
  };
};

// Protected route component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
  fallback?: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  fallback = <div className="flex items-center justify-center min-h-screen">Access denied</div>
}) => {
  const { isAuthenticated, isLoading, hasAnyRole } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return fallback;
  }

  if (requiredRoles.length > 0 && !hasAnyRole(requiredRoles)) {
    return fallback;
  }

  return <>{children}</>;
};

export default IframeAuthProvider;